import React, { useState } from 'react';
import { CameraConfig } from '../../core/sensors/camera/capture';
import { ColorMarkerConfig } from '../../core/types/tracking';
import { AudioSensorConfig } from '../../core/types/audio';
import { DeviceInfo } from '../../core/types/session';
import { Copy, RefreshCw } from 'lucide-react';

interface StepWizardProps {
  videoDevices: DeviceInfo[];
  audioDevices: DeviceInfo[];
  cameraConfigA: CameraConfig;
  cameraConfigB: CameraConfig;
  markerConfigA: ColorMarkerConfig;
  markerConfigB: ColorMarkerConfig;
  audioConfig: AudioSensorConfig;
  isStereoMode: boolean;
  onUpdateCameraA: (cfg: Partial<CameraConfig>) => void;
  onUpdateCameraB: (cfg: Partial<CameraConfig>) => void;
  onUpdateMarkerA: (cfg: Partial<ColorMarkerConfig>) => void;
  onUpdateMarkerB: (cfg: Partial<ColorMarkerConfig>) => void;
  onUpdateAudio: (cfg: Partial<AudioSensorConfig>) => void;
  onCopyMarkerAToB: () => void;
  onRefreshDevices: () => void;
  onExportCsv: (type: 'visual' | 'audio' | 'fused' | 'json') => void;
}

export const StepWizard: React.FC<StepWizardProps> = ({
  videoDevices,
  audioDevices,
  cameraConfigA,
  cameraConfigB,
  markerConfigA,
  markerConfigB,
  audioConfig,
  isStereoMode,
  onUpdateCameraA,
  onUpdateCameraB,
  onUpdateMarkerA,
  onUpdateMarkerB,
  onUpdateAudio,
  onCopyMarkerAToB,
  onRefreshDevices,
  onExportCsv,
}) => {
  const [activeStep, setActiveStep] = useState<number>(1);

  return (
    <div className="sidebar-wizard">
      <div className="wizard-nav">
        {['设备', '标记', '音频', '标定', '采集', '导出'].map((label, idx) => (
          <div
            key={idx}
            className={`wizard-step-tab ${activeStep === idx + 1 ? 'active' : ''}`}
            onClick={() => setActiveStep(idx + 1)}
          >
            {idx + 1}. {label}
          </div>
        ))}
      </div>

      <div className="wizard-content">
        {/* STEP 1: DEVICES */}
        {activeStep === 1 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: '0.9rem' }}>设备选择</h3>
              <button className="btn btn-outline" style={{ padding: '4px 8px', fontSize: '0.75rem' }} onClick={onRefreshDevices}>
                <RefreshCw size={12} /> 刷新
              </button>
            </div>

            <div className="alert-box alert-info">
              💡 必须在 HTTPS 或 localhost 环境下触发媒体权限。
            </div>

            <div className="form-group">
              <label className="form-label">摄像头 A (Camera A)</label>
              <select
                className="form-select"
                value={cameraConfigA.deviceId || ''}
                onChange={(e) => onUpdateCameraA({ deviceId: e.target.value })}
              >
                <option value="">默认 / 自动选择</option>
                {videoDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>

            {isStereoMode && (
              <div className="form-group">
                <label className="form-label">摄像头 B (Camera B)</label>
                <select
                  className="form-select"
                  value={cameraConfigB.deviceId || ''}
                  onChange={(e) => onUpdateCameraB({ deviceId: e.target.value })}
                >
                  <option value="">请选择不同设备...</option>
                  {videoDevices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId} disabled={d.deviceId === cameraConfigA.deviceId}>
                      {d.label} {d.deviceId === cameraConfigA.deviceId ? '(已被 Cam A 选择)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">麦克风 (Microphone)</label>
              <select
                className="form-select"
                value={audioConfig.deviceId || ''}
                onChange={(e) => onUpdateAudio({ deviceId: e.target.value })}
              >
                <option value="">默认麦克风</option>
                {audioDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* STEP 2: MARKER */}
        {activeStep === 2 && (
          <div>
            <h3 style={{ fontSize: '0.9rem', marginBottom: 12 }}>颜色标记设置 (Tennis Ball HSV)</h3>

            <div className="form-group">
              <label className="form-label">HSV 色相范围 (Hue Low - High)</label>
              <div className="slider-group">
                <input
                  type="range"
                  min="0"
                  max="180"
                  value={markerConfigA.hsvLower[0]}
                  onChange={(e) =>
                    onUpdateMarkerA({
                      hsvLower: [parseInt(e.target.value), markerConfigA.hsvLower[1], markerConfigA.hsvLower[2]],
                    })
                  }
                />
                <span className="slider-val">{markerConfigA.hsvLower[0]}</span>
              </div>
              <div className="slider-group" style={{ marginTop: 4 }}>
                <input
                  type="range"
                  min="0"
                  max="180"
                  value={markerConfigA.hsvUpper[0]}
                  onChange={(e) =>
                    onUpdateMarkerA({
                      hsvUpper: [parseInt(e.target.value), markerConfigA.hsvUpper[1], markerConfigA.hsvUpper[2]],
                    })
                  }
                />
                <span className="slider-val">{markerConfigA.hsvUpper[0]}</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">最小圆度 (Min Circularity: {markerConfigA.minCircularity})</label>
              <div className="slider-group">
                <input
                  type="range"
                  min="0.1"
                  max="0.9"
                  step="0.05"
                  value={markerConfigA.minCircularity}
                  onChange={(e) => onUpdateMarkerA({ minCircularity: parseFloat(e.target.value) })}
                />
                <span className="slider-val">{markerConfigA.minCircularity}</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">平滑系数 (Smoothing Alpha: {markerConfigA.smoothingAlpha})</label>
              <div className="slider-group">
                <input
                  type="range"
                  min="0.05"
                  max="1.0"
                  step="0.05"
                  value={markerConfigA.smoothingAlpha}
                  onChange={(e) => onUpdateMarkerA({ smoothingAlpha: parseFloat(e.target.value) })}
                />
                <span className="slider-val">{markerConfigA.smoothingAlpha}</span>
              </div>
            </div>

            {isStereoMode && (
              <button className="btn btn-outline" style={{ width: '100%', marginTop: 8 }} onClick={onCopyMarkerAToB}>
                <Copy size={14} /> 复制 Marker A 参数到 Marker B
              </button>
            )}
          </div>
        )}

        {/* STEP 3: AUDIO */}
        {activeStep === 3 && (
          <div>
            <h3 style={{ fontSize: '0.9rem', marginBottom: 12 }}>音频特征配置</h3>

            <div className="form-group">
              <label className="form-label">分析窗口 (Window Size: {audioConfig.windowSizeMs} ms)</label>
              <input
                type="number"
                className="form-input"
                value={audioConfig.windowSizeMs}
                onChange={(e) => onUpdateAudio({ windowSizeMs: parseInt(e.target.value) || 100 })}
              />
            </div>

            <div className="alert-box alert-warning">
              ⚠️ 强行关闭 Echo Cancellation / AGC / Noise Suppression 以保持原始物理声级，若浏览器不支持将自动标注标志。
            </div>
          </div>
        )}

        {/* STEP 4: CALIBRATION */}
        {activeStep === 4 && (
          <div>
            <h3 style={{ fontSize: '0.9rem', marginBottom: 12 }}>双摄标定 (Stereo Calibration)</h3>

            <div className="alert-box alert-info">
              阶段一目前为独立 2D 追踪。极线校正与 3D 三角测量需导入 valid 棋盘格标定 JSON 文件。
            </div>

            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              当前状态: <strong style={{ color: 'var(--warning-color)' }}>未标定 (Uncalibrated)</strong>
            </div>
          </div>
        )}

        {/* STEP 5: CAPTURE */}
        {activeStep === 5 && (
          <div>
            <h3 style={{ fontSize: '0.9rem', marginBottom: 12 }}>实验采集控制</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              所有摄像头与麦克风数据纯本地计算，无任何云端上传。
            </p>
          </div>
        )}

        {/* STEP 6: EXPORT */}
        {activeStep === 6 && (
          <div>
            <h3 style={{ fontSize: '0.9rem', marginBottom: 12 }}>实验数据导出</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-outline" onClick={() => onExportCsv('visual')}>
                导出视觉轨迹 CSV
              </button>
              <button className="btn btn-outline" onClick={() => onExportCsv('audio')}>
                导出音频特征 CSV
              </button>
              <button className="btn btn-primary" onClick={() => onExportCsv('fused')}>
                导出融合实验 CSV
              </button>
              <button className="btn btn-success" onClick={() => onExportCsv('json')}>
                导出完整会话 JSON
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
